import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, TrendingUp, TrendingDown, DollarSign, AlertCircle, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface RecurringEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  due_day: number;
  is_active: boolean;
  created_at: string;
  client_id?: string;
  case_id?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
}

interface RecurringForecastProps {
  className?: string;
}

const RecurringForecast = ({ className }: RecurringForecastProps) => {
  const [recurringEntries, setRecurringEntries] = useState<RecurringEntry[]>([]);
  const [paidRecurringIds, setPaidRecurringIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchRecurringEntries();
    fetchPaidRecurringEntries();
  }, [currentMonth]);

  const fetchPaidRecurringEntries = async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      
      console.log('Fetching paid recurring entries for:', { month, year });
      
      const { data, error } = await supabase
        .from('recurring_payments')
        .select('recurring_entry_id')
        .eq('payment_month', month)
        .eq('payment_year', year);

      if (error) {
        console.error('Error fetching paid recurring entries:', error);
        return;
      }

      const paidIds = new Set(data?.map(payment => payment.recurring_entry_id) || []);
      console.log('Paid recurring IDs for current month:', paidIds);
      setPaidRecurringIds(paidIds);
    } catch (error) {
      console.error('Error in fetchPaidRecurringEntries:', error);
    }
  };

  const fetchRecurringEntries = async () => {
    try {
      const { data, error } = await supabase
        .from('recurring_entries' as any)
        .select(`
          *,
          clients:client_id (
            name,
            email
          ),
          cases:case_id (
            title,
            case_number
          )
        `)
        .eq('is_active', true)
        .order('due_day', { ascending: true });

      if (error) throw error;
      setRecurringEntries((data as unknown as RecurringEntry[]) || []);
    } catch (error) {
      console.error('Erro ao buscar entradas recorrentes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const calculateMonthlyForecast = () => {
    // Filtrar entradas ativas que não foram pagas no mês atual
    const activeUnpaidEntries = recurringEntries.filter(entry => 
      entry.is_active && !paidRecurringIds.has(entry.id)
    );
    
    const revenues = activeUnpaidEntries
      .filter(entry => entry.entry_type === 'receita')
      .reduce((sum, entry) => sum + entry.amount, 0);
    
    const expenses = activeUnpaidEntries
      .filter(entry => entry.entry_type === 'despesa')
      .reduce((sum, entry) => sum + entry.amount, 0);
    
    return {
      revenues,
      expenses,
      profit: revenues - expenses,
      totalEntries: activeUnpaidEntries.length
    };
  };

  const getEntriesByDay = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const entriesByDay: { [key: number]: RecurringEntry[] } = {};
    
    // Inicializar todos os dias
    for (let day = 1; day <= daysInMonth; day++) {
      entriesByDay[day] = [];
    }
    
    // Adicionar entradas aos dias correspondentes
    recurringEntries
      .filter(entry => entry.is_active)
      .forEach(entry => {
        if (entry.due_day <= daysInMonth) {
          if (!entriesByDay[entry.due_day]) {
            entriesByDay[entry.due_day] = [];
          }
          entriesByDay[entry.due_day].push(entry);
        }
      });
    
    return entriesByDay;
  };

  const getUpcomingEntries = () => {
    const today = new Date();
    const currentDay = today.getDate();
    const daysInMonth = getDaysInMonth(currentMonth);
    const entriesByDay = getEntriesByDay();
    
    const upcoming: RecurringEntry[] = [];
    
    for (let day = currentDay; day <= daysInMonth; day++) {
      if (entriesByDay[day]) {
        // Filtrar entradas que não foram pagas no mês atual
        const unpaidEntries = entriesByDay[day].filter(entry => !paidRecurringIds.has(entry.id));
        upcoming.push(...unpaidEntries);
      }
    }
    
    return upcoming.sort((a, b) => a.due_day - b.due_day);
  };

  const getOverdueEntries = () => {
    const today = new Date();
    const currentDay = today.getDate();
    const month = currentMonth.getMonth();
    const year = currentMonth.getFullYear();
    
    // Only show overdue for current month
    if (month !== today.getMonth() || year !== today.getFullYear()) {
      return [];
    }
    
    const entriesByDay = getEntriesByDay();
    
    const overdue: RecurringEntry[] = [];
    
    for (let day = 1; day < currentDay; day++) {
      if (entriesByDay[day]) {
        // Filtrar entradas vencidas que não foram pagas no mês atual
        const unpaidEntries = entriesByDay[day].filter(entry => !paidRecurringIds.has(entry.id));
        overdue.push(...unpaidEntries);
      }
    }
    
    return overdue.sort((a, b) => a.due_day - b.due_day);
  };

  const forecast = calculateMonthlyForecast();
  const upcomingEntries = getUpcomingEntries();
  const overdueEntries = getOverdueEntries();

  if (loading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const brl = (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  const activeCount = recurringEntries.filter(e => e.is_active).length;
  const pendingActive = recurringEntries.filter(e => e.is_active && !paidRecurringIds.has(e.id)).length;

  const renderEntryRow = (entry: RecurringEntry, overdue = false) => {
    const isRevenue = entry.entry_type === 'receita';
    return (
      <div key={entry.id} className="flex items-center gap-4 px-5 py-3">
        <span className={`h-9 w-1 shrink-0 rounded-full ${overdue ? 'bg-red-500' : isRevenue ? 'bg-emerald-500' : 'bg-[#c4ab83]'}`} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">{entry.description}</p>
          <p className={`mt-0.5 text-xs ${overdue ? 'font-medium text-red-600' : 'text-slate-500'}`}>
            {[entry.category, overdue ? `Venceu dia ${entry.due_day}` : `${isRevenue ? 'Recebe' : 'Vence'} dia ${entry.due_day}`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <span className={`whitespace-nowrap text-sm font-semibold tabular-nums ${isRevenue ? 'text-emerald-600' : 'text-red-600'}`}>
          {isRevenue ? '+' : '−'} {brl(entry.amount)}
        </span>
      </div>
    );
  };

  return (
    <div className={`space-y-5 ${className}`}>
      {/* Cabeçalho */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="relative flex flex-col gap-4 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-5 py-6 sm:flex-row sm:items-end sm:justify-between sm:px-6">
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-3 top-1/2 h-32 w-32 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <div className="relative flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#c4ab83]/20 text-[#9a7f52]">
              <Calendar className="h-6 w-6" />
            </span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Previsão do mês</p>
              <p className="mt-1 text-3xl font-semibold capitalize tracking-tight text-slate-900">{getMonthName(currentMonth)}</p>
              <p className="mt-1 text-sm text-slate-500">
                Com base em {activeCount} {activeCount === 1 ? 'receita ou despesa fixa ativa' : 'receitas e despesas fixas ativas'}.
              </p>
            </div>
          </div>
          <div className="relative inline-flex items-center rounded-xl bg-white p-1 shadow-sm ring-1 ring-slate-200">
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              ← Anterior
            </button>
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date())}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#7a6440] hover:bg-[#faf7f2]"
            >
              Hoje
            </button>
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              Próximo →
            </button>
          </div>
        </div>

        {/* Totais */}
        <div className="grid grid-cols-1 gap-px bg-slate-100 sm:grid-cols-3">
          <div className="bg-white px-5 py-4 sm:px-6">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-500" /> A receber
            </p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${forecast.revenues ? 'text-emerald-600' : 'text-slate-300'}`}>
              {brl(forecast.revenues)}
            </p>
            <p className="mt-0.5 text-[11px] text-slate-400">
              {recurringEntries.filter(e => e.is_active && e.entry_type === 'receita' && !paidRecurringIds.has(e.id)).length} receitas fixas
            </p>
          </div>
          <div className="bg-white px-5 py-4 sm:px-6">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              <TrendingDown className="h-3.5 w-3.5 text-red-500" /> A pagar
            </p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${forecast.expenses ? 'text-red-600' : 'text-slate-300'}`}>
              {brl(forecast.expenses)}
            </p>
            <p className="mt-0.5 text-[11px] text-slate-400">
              {recurringEntries.filter(e => e.is_active && e.entry_type === 'despesa' && !paidRecurringIds.has(e.id)).length} despesas fixas
            </p>
          </div>
          <div className="bg-white px-5 py-4 sm:px-6">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              <DollarSign className="h-3.5 w-3.5 text-[#9a7f52]" /> Saldo previsto
            </p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${
              forecast.profit > 0 ? 'text-emerald-600' : forecast.profit < 0 ? 'text-red-600' : 'text-slate-300'
            }`}>
              {brl(forecast.profit)}
            </p>
            <p className="mt-0.5 text-[11px] text-slate-400">
              {forecast.profit > 0 ? 'Sobra no mês' : forecast.profit < 0 ? 'Falta no mês' : 'Sem movimento previsto'}
            </p>
          </div>
        </div>
      </div>

      {/* Vencidas */}
      {overdueEntries.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-red-600">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
              Vencidas e não pagas
            </p>
            <span className="text-xs text-slate-400">{overdueEntries.length}</span>
          </div>
          <div className="divide-y divide-slate-100">{overdueEntries.map((entry) => renderEntryRow(entry, true))}</div>
        </div>
      )}

      {/* Próximas */}
      {upcomingEntries.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Próximas do mês</p>
            <span className="text-xs text-slate-400">{upcomingEntries.length}</span>
          </div>
          <div className="divide-y divide-slate-100">{upcomingEntries.map((entry) => renderEntryRow(entry))}</div>
        </div>
      )}

      {/* Sem pendências */}
      {pendingActive === 0 && (
        <div className="flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white p-5">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${activeCount > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
            <Calendar className="h-5 w-5" />
          </span>
          <div>
            <p className="font-semibold text-slate-900">
              {activeCount > 0 ? 'Tudo pago neste mês' : 'Nenhuma receita ou despesa fixa ativa'}
            </p>
            <p className="text-sm text-slate-500">
              {activeCount > 0
                ? 'Todas as receitas e despesas fixas do mês já foram confirmadas.'
                : 'Cadastre na aba Recorrentes os valores que se repetem todo mês para ver a previsão aqui.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurringForecast;